import { Badge, LegacyCard, Text } from "@shopify/polaris"
import { observer } from "mobx-react"
import { useVisibleChange } from "polaris-enhance"
import { useState } from "react"
import { useHistory } from "react-router-dom"

import APIDashboard, { ReportAppEmbedKind } from "@/api/dashboard"
import { IAppEmbedStatus } from "@/api/dashboard/type"
import stores from "@/stores"
import { isShopifyEnv } from "@/utils/env-helper"

type ExtensionActivation = { status?: string }
type ExtensionInfo = {
  handle: string
  type: "ui_extension" | "theme_app_extension" | string
  activations?: ExtensionActivation[]
}

const getEnabledByHandle = (
  extensions: ExtensionInfo[],
  handle: string,
): boolean => {
  const ext = extensions.find((item) => item.handle === handle)
  if (!ext) return false
  if (ext.type === "theme_app_extension") {
    return !!ext.activations?.some(
      (activation) => activation.status === "active",
    )
  }
  return (ext.activations?.length ?? 0) > 0
}

const AppEmbedStatusCard = () => {
  const history = useHistory()
  const { ReturnRequestsDataStore, UserInfoStore } = stores

  const isPlus = !!UserInfoStore.data.display_name
    ?.toLowerCase()
    ?.includes("plus")

  const [frontendStatus, setFrontendStatus] = useState<IAppEmbedStatus | null>(
    null,
  )

  // 前端 shopify 获取的状态与后端状态不一致时，上报后端更新（仅按钮与独立小部件支持上报）
  const reportStatusDiff = (nextStatus: IAppEmbedStatus) => {
    const backendStatus = ReturnRequestsDataStore.app_embed_status
    if (!backendStatus) return

    const diffList: { kind: ReportAppEmbedKind; enabled: boolean }[] = []
    if (
      nextStatus.return_button_enabled !== backendStatus.return_button_enabled
    ) {
      diffList.push({
        kind: ReportAppEmbedKind.returnButton,
        enabled: nextStatus.return_button_enabled,
      })
    }
    if (
      nextStatus.standalone_widget_enabled !==
      backendStatus.standalone_widget_enabled
    ) {
      diffList.push({
        kind: ReportAppEmbedKind.standaloneWidget,
        enabled: nextStatus.standalone_widget_enabled,
      })
    }

    diffList.forEach(({ kind, enabled }) => {
      APIDashboard.reportAppEmbed({ kind, enabled }).catch(() => {})
    })
  }

  // 挂载时检测一次，并在浏览器 tab 切回可见时重新检测
  useVisibleChange(async (isShow) => {
    if (isShow === false) return

    const shopify = (window as any).shopify
    if (!isShopifyEnv || !shopify?.app?.extensions) return

    try {
      const extensions: ExtensionInfo[] = await shopify.app.extensions()
      const nextStatus: IAppEmbedStatus = {
        return_button_enabled: getEnabledByHandle(
          extensions,
          "return-order-button",
        ),
        standalone_widget_enabled: getEnabledByHandle(
          extensions,
          "Returns-widget",
        ),
        return_protection_enabled: getEnabledByHandle(
          extensions,
          isPlus ? "Free-return-service" : "theme-app-extension",
        ),
      }
      setFrontendStatus(nextStatus)
      reportStatusDiff(nextStatus)
    } catch {
      setFrontendStatus(null)
    }
  }, true)

  const {
    return_button_enabled,
    standalone_widget_enabled,
    return_protection_enabled,
  } = frontendStatus ?? ReturnRequestsDataStore.app_embed_status

  const items = [
    {
      label: t("RequestReturnButton"),
      enabled: return_button_enabled,
      onAction: () => {
        history.push("/return-page", { focus: "embedded_returns" })
      },
    },
    {
      label: t("ReturnsStandaloneWidget"),
      enabled: standalone_widget_enabled,
      onAction: () => {
        history.push("/return-page", { focus: "embedded_returns" })
      },
    },
    {
      label: t("ReturnProtection"),
      enabled: return_protection_enabled,
      onAction: () => {
        history.push("/free-return-service", {
          focus: "free_return_service_widget",
        })
      },
    },
  ]

  return (
    <LegacyCard sectioned>
      <div className="space-y-1">
        <Text variant="headingSm" as="h2">
          {t("AppEmbedAppBlocksStatus")}
        </Text>
        <Text variant="bodyMd" as="p" tone="subdued">
          {t("JustForDisplayOnlyRequiredWhenFeaturesUsed")}
        </Text>
      </div>
      <div className="mt-4 flex flex-col gap-4 md:flex-row">
        {items.map((item) => (
          <div
            key={item.label}
            className="flex flex-1 cursor-pointer items-center justify-between gap-2 rounded-lg border border-solid border-[#e3e3e3] px-4 py-3 hover:bg-[#f7f7f7]"
            onClick={item.onAction}
          >
            <Text variant="bodyMd" as="span">
              {item.label}
            </Text>
            <Badge tone={item.enabled ? "success" : undefined}>
              {item.enabled ? t("Added") : t("NotAdded")}
            </Badge>
          </div>
        ))}
      </div>
    </LegacyCard>
  )
}

export default observer(AppEmbedStatusCard)
